import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { HelpLink } from "../app/HelpLink";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { clsx } from "clsx";
import { useCoreDoc } from "../app/useCoreDoc";
import { useReadOnly } from "../app/useReadOnly";
import { DateNav } from "../app/DateNav";
import { useNow } from "../core/useNow";
import { dayKey } from "./ops";
import { Planner } from "./Planner";
import { useLayout } from "../app/layout";

type View = "day" | "week";
const VIEW_KEY = "drang.planner.view";
const HOURS = Array.from({ length: 16 }, (_, i) => 6 + i);

function storedView(): View {
  try {
    return localStorage.getItem(VIEW_KEY) === "week" ? "week" : "day";
  } catch {
    return "day";
  }
}

/** Monday of the week that holds `d` (weeks start on Monday, as in the habits widget). */
function mondayOf(d: Date): Date {
  const m = new Date(d);
  m.setHours(12, 0, 0, 0);
  m.setDate(m.getDate() - ((m.getDay() + 6) % 7));
  return m;
}

const addDays = (d: Date, n: number) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

/** Days a week view shows in a column this wide: the whole week from ~85 px a day, else three (docs/plans/flexui.md). */
const WEEK_MIN_PX = 640;

/**
 * The Planner page (spec §9.6 timeboxing): the planner (`Planner`, also the Week widget) as a day or a week, with
 * the day/week choice and the date navigation. The view is remembered per device.
 * The week adapts to the room it has: seven days, or three with ‹ › when the column is narrower (a medium screen,
 * an open task panel); a narrow screen shows one day.
 */
export function PlannerPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { date } = useParams();
  const [params] = useSearchParams();
  const narrow = useLayout().mode === "narrow";
  const asked: View =
    params.get("view") === "week" ? "week" : params.get("view") === "day" ? "day" : storedView();
  const view: View = narrow ? "day" : asked;
  // the grid's own width decides between the whole week and three days
  const column = useRef<HTMLDivElement>(null);
  const [roomy, setRoomy] = useState(true);
  useLayoutEffect(() => {
    const el = column.current;
    if (!el) return;
    // the trays take about 260 px beside the grid
    const measure = () => setRoomy(el.clientWidth >= WEEK_MIN_PX + 260);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const span = view === "day" ? 1 : roomy ? 7 : 3;
  const now = useNow();
  const todayKey = dayKey(new Date(now));
  const anchor = useMemo(() => new Date((date ?? todayKey) + "T12:00:00"), [date, todayKey]);
  const days = useMemo(
    () =>
      span === 7
        ? Array.from({ length: 7 }, (_, i) => dayKey(addDays(mondayOf(anchor), i)))
        : Array.from({ length: span }, (_, i) => dayKey(addDays(anchor, i))),
    [span, anchor],
  );
  const { handle, workspaceId } = useCoreDoc();
  const ro = useReadOnly(handle, workspaceId);

  const go = (d: Date, v: View = view) => {
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch {
      /* per-device convenience only */
    }
    navigate(`/planner/${dayKey(d)}?view=${v}`);
  };
  const step = span;
  const current = view === "day" ? days[0] === todayKey : days.includes(todayKey);
  const fmt = (d: string, o: Intl.DateTimeFormatOptions) =>
    new Date(d + "T12:00:00").toLocaleDateString(i18n.language, o);
  const label =
    view === "day"
      ? fmt(days[0], { weekday: "long", day: "numeric", month: "long" })
      : `${fmt(days[0], { day: "numeric", month: "short" })} – ${fmt(days[days.length - 1], { day: "numeric", month: "short", year: "numeric" })}`;

  return (
    <div
      className={clsx(
        "mx-auto flex w-full flex-col gap-6 px-4 pb-16 pt-5 mid:px-9 mid:pt-8",
        view === "week" ? "max-w-7xl" : "max-w-5xl",
      )}
      data-testid="planner"
      data-view={view}
      data-span={span}
    >
      <div className="min-w-0 flex-1" ref={column}>
        <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2">
          <h1 className="text-[26px] font-semibold">{t("nav.planner")}</h1>
          <HelpLink page="guides/planner/" />
          {!narrow && (
            <div
              className="flex rounded-md border border-neutral-200 bg-[var(--surface)] p-0.5 text-[13px] dark:border-neutral-800"
              role="group"
              aria-label={t("planner.view")}
            >
              {(["day", "week"] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  aria-pressed={view === v}
                  onClick={() => go(anchor, v)}
                  data-testid={`planner-view-${v}`}
                  className="rounded-sm px-3 py-1 text-neutral-600 hover:bg-neutral-100 aria-pressed:bg-neutral-100 aria-pressed:text-neutral-900 dark:text-neutral-300 dark:hover:bg-neutral-800 dark:aria-pressed:bg-neutral-800 dark:aria-pressed:text-neutral-100"
                >
                  {t(`planner.${v}`)}
                </button>
              ))}
            </div>
          )}
          <div className="ml-auto">
            <DateNav
              label={label}
              current={current}
              currentLabel={view === "day" ? t("planner.today") : t("planner.thisWeek")}
              prevLabel={view === "day" ? t("worklog.previousDay") : t("planner.previousWeek")}
              nextLabel={view === "day" ? t("worklog.nextDay") : t("planner.nextWeek")}
              onPrev={() => go(addDays(anchor, -step))}
              onNext={() => go(addDays(anchor, step))}
              onCurrent={() => go(new Date(now))}
              testId="planner-nav"
            />
          </div>
        </div>

        <Planner
          handle={handle}
          workspaceId={workspaceId ?? ""}
          readOnly={ro}
          days={days}
          hours={HOURS}
          layout={view}
          todayKey={todayKey}
          now={now}
          narrowTrays="below"
          openTask={(id, ws) =>
            ws
              ? navigate(`/list?workspace=${encodeURIComponent(ws)}&task=${encodeURIComponent(id)}`)
              : navigate(`?view=${view}&task=${id}`)
          }
          openDay={(d) => go(new Date(d + "T12:00:00"), "day")}
        />
      </div>
    </div>
  );
}
